// Blog content, read from the backend rather than bundled into the app, so a
// post can be edited at /admin/blog without a deploy.

const base = process.env.REACT_APP_BACKEND_URL || '';

/** Published posts, in the running order set in the admin page. */
export const fetchPosts = async () => {
    const res = await fetch(`${base}/api/blog/posts`);
    if (!res.ok) throw new Error('Could not load posts');
    const data = await res.json();
    return data.posts || [];
};

/** One post by slug. Drafts come back too, flagged unpublished. */
export const fetchPost = async (slug) => {
    const res = await fetch(`${base}/api/blog/posts/${encodeURIComponent(slug)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Could not load the post');
    return res.json();
};

/**
 * Images are served by the backend, so a stored path needs the API host in
 * front of it. Anything already absolute (or a data URL from a preview) is
 * left alone, as are the few images that still live in /public.
 */
export const imageSrc = (image) => {
    if (!image) return null;
    if (/^(https?:|data:)/.test(image)) return image;
    if (image.startsWith('/api/')) return `${base}${image}`;
    return image;
};

// ── Admin ────────────────────────────────────────────────────────────────────

const adminFetch = (token, path, opts = {}) => fetch(`${base}${path}`, {
    ...opts,
    headers: {
        Authorization: `Bearer ${token}`,
        ...(opts.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(opts.headers || {}),
    },
});

export const adminListPosts = async (token) => {
    const res = await adminFetch(token, '/api/admin/blog/posts');
    if (res.status === 401) throw new Error('unauthorised');
    if (!res.ok) throw new Error('Could not load posts');
    const data = await res.json();
    return data.posts || [];
};

const sendPost = async (res) => {
    if (res.status === 401) throw new Error('unauthorised');
    if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Save failed');
    }
    return res.json();
};

export const adminCreatePost = async (token, post) =>
    sendPost(await adminFetch(token, '/api/admin/blog/posts', { method: 'POST', body: JSON.stringify(post) }));

export const adminUpdatePost = async (token, id, post) =>
    sendPost(await adminFetch(token, `/api/admin/blog/posts/${id}`, { method: 'PATCH', body: JSON.stringify(post) }));

export const adminDeletePost = async (token, id) =>
    sendPost(await adminFetch(token, `/api/admin/blog/posts/${id}`, { method: 'DELETE' }));

/**
 * Shrink a picture in the browser before it is uploaded.
 *
 * A photo straight off a phone is several megabytes and far larger than the
 * 760px column it will ever be shown in, so it is resized to 1200px wide and
 * re-encoded as JPEG here. That keeps the upload quick on a slow connection
 * and keeps the database small.
 */
export const resizeImage = (file, maxWidth = 1200, quality = 0.85) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        // A transparent PNG would otherwise turn black when saved as JPEG.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
            (blob) => (blob ? resolve({ blob, width: canvas.width, height: canvas.height }) : reject(new Error('Could not read that image'))),
            'image/jpeg',
            quality,
        );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image')); };
    img.src = url;
});

export const adminUploadImage = async (token, file, quality = 0.85) => {
    const { blob, width, height } = await resizeImage(file, 1200, quality);
    const form = new FormData();
    form.append('file', blob, (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg');
    const res = await adminFetch(token, '/api/admin/blog/images', { method: 'POST', body: form });
    const data = await sendPost(res);
    // Portrait pictures are shown whole on a post page rather than cropped
    // into a banner, so the shape is worth recording at upload time.
    return { ...data, portrait: height > width, width, height };
};
